/**
 * 0.36.169 - SearchField element.
 *
 * Compound search input: a left-rounded text input glued flush to a
 * right-rounded button. Two default variants:
 *   - 'icon': button shows a magnifying glass (54px wide)
 *   - 'text': button shows the word "Search" (83px wide)
 *
 * Behavior when populated: as soon as the input has any text, the
 * right button swaps to a 54px-wide "X" (clear) regardless of the
 * default variant. Clicking the X clears the input and the original
 * variant returns.
 *
 * Border tokens (exact palette match):
 *   --color-text-600 = #747474  (the spec's "Text Black 600" border)
 *
 * Focus: relies on the global *:focus-visible magenta ring.
 *
 * Accessibility:
 *   - Magnifying-glass button: aria-label="Search"
 *   - X button:               aria-label="Clear text"
 *   - Text "Search" button:   visible label (no aria override needed)
 */
import { useState, useEffect, useRef } from 'react';
import styles from './SearchField.module.css';

const MagnifyingGlassIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" aria-hidden="true">
    {/* Lens */}
    <circle cx="7" cy="7" r="4.5" fill="none" stroke="currentColor"
            strokeWidth="1.5" />
    {/* Handle */}
    <path d="M10.2 10.2 L 14 14" stroke="currentColor"
          strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const ClearXIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" aria-hidden="true">
    <path d="M4 4 L 12 12 M 12 4 L 4 12" stroke="currentColor"
          strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);


export default function SearchField({
  defaultVariant = 'icon',  // 'icon' | 'text'
  buttonLabel = 'Search',
  placeholder = '',
  // 0.36.331 → 0.36.332 — Default reverted to 40 per user request.
  // The 42 alignment was the wrong direction; instead HenaketDropdown
  // moves to exact 40 (see 0.36.332 in HenaketDropdown.module.css).
  // 40px is the canonical visible height for input controls; the
  // dropdown's path to 40 uses padding 7 (mathematically required
  // for 1px border + 24 line-box).
  heightPx = 40,
  widthPx = 350,
  value: controlledValue,
  defaultValue = '',
  onChange,
  onSearch,
  onClear,
  name,
  id,
}) {
  // Optional-controlled pattern: if `value` prop is provided, parent
  // owns the value. Otherwise we own it internally.
  const isControlled = controlledValue !== undefined;
  const [internalValue, setInternalValue] = useState(defaultValue);
  const value = isControlled ? controlledValue : internalValue;

  const inputRef = useRef(null);

  // Keep internal in sync if parent toggles between controlled/uncontrolled
  // (rare, but defensive — happens during form-reset flows).
  useEffect(() => {
    if (!isControlled) return;
  }, [controlledValue, isControlled]);

  const setValue = (v) => {
    if (!isControlled) setInternalValue(v);
    if (typeof onChange === 'function') onChange(v);
  };

  const handleSubmit = () => {
    if (typeof onSearch === 'function') onSearch(value);
  };

  const handleClear = () => {
    setValue('');
    if (typeof onClear === 'function') onClear();
    // Return focus to the input after clearing (expected UX)
    if (inputRef.current) inputRef.current.focus();
  };

  // When input has content, button always becomes the X clear button
  // regardless of the configured defaultVariant.
  const hasContent = value !== '';

  // Right button: dimensions depend on whether it's the icon variant
  // (54px) or text variant (83px). X always uses icon dimensions.
  const showAsIcon = hasContent || defaultVariant === 'icon';
  const buttonClass = showAsIcon ? styles.iconBtn : styles.textBtn;
  // 0.45.57 - The icon (16px) and its button (54px) are sized for the
  // 40px height and grow or shrink with it (they stayed 16px and 54px
  // at any height).
  // 0.45.58 - At half the rate of the height: +100% height gives +50%
  // (80px: a 24px icon in an 81px button).
  const scale = 1 + ((Number(heightPx) || 40) / 40 - 1) * 0.5;
  const iconSize = Math.round(16 * scale);
  const iconBtnStyle = { height: `${heightPx}px`, width: `${Math.round(54 * scale)}px`, padding: 0 };

  return (
    <div
      className={styles.container}
      style={{
        width: `${widthPx}px`,
        height: `${heightPx}px`,
      }}
    >
      <input
        ref={inputRef}
        type="text"
        className={styles.input}
        style={{ height: `${heightPx}px` }}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            handleSubmit();
          }
        }}
        placeholder={placeholder}
        name={name}
        id={id}
      />
      {hasContent ? (
        <button
          type="button"
          className={buttonClass}
          style={iconBtnStyle}
          onClick={handleClear}
          aria-label="Clear text"
        >
          <ClearXIcon size={iconSize} />
        </button>
      ) : defaultVariant === 'icon' ? (
        <button
          type="button"
          className={buttonClass}
          style={iconBtnStyle}
          onClick={handleSubmit}
          aria-label="Search"
        >
          <MagnifyingGlassIcon size={iconSize} />
        </button>
      ) : (
        <button
          type="button"
          className={buttonClass}
          style={{ height: `${heightPx}px` }}
          onClick={handleSubmit}
        >
          {buttonLabel}
        </button>
      )}
    </div>
  );
}
